import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { decryptId } from "../helper/idCodec.js";
import { getData, API_ENDPOINTS } from "../../services/apiService.js";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Divider,
    Grid,
    Button,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentIcon from "@mui/icons-material/Payment";
import MapIcon from "@mui/icons-material/Map";
import RoomIcon from "@mui/icons-material/Room";
import FlagIcon from "@mui/icons-material/Flag";
import LoadingSpinner from "../LoadingContext"; // ✅ your spinner component
import "./Packages.css";

function PackagesInfo() {
    const { id } = useParams();
    const navigate = useNavigate();
    const packageId = id ? decryptId(id) : "";
    const [packageData, setPackageData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (packageId) {
            fetchPackageDetails();
        }
    }, [packageId]);

    const fetchPackageDetails = async () => {
        try {
            setLoading(true);
            const startTime = Date.now();

            const res = await getData(API_ENDPOINTS.fetchPackageById, { package_id: packageId });

            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 3000 - elapsed); // ⏱️ enforce minimum 3 sec delay

            setTimeout(() => {
                if (res?.statusCode === 200) {
                    setPackageData(res.result);
                } else {
                    console.error("Unexpected response:", res);
                }
                setLoading(false);
            }, remaining);
        } catch (error) {
            console.error("Error fetching package:", error);
            setTimeout(() => setLoading(false), 3000); // ⏱️ still enforce min 3 sec on error
        }
    };

    if (loading) {
        return <LoadingSpinner isLoading={loading} />;
    }

    if (!packageData) {
        return (
            <Box sx={{ textAlign: "center", mt: 10 }}>
                <Typography variant="h6">No package details found.</Typography>
            </Box>
        );
    }

    const mapping = packageData?.mappings?.[0] || {};
    const trip = mapping?.trip;
    const truck = mapping?.truck;
    const payment = mapping?.payment;
    const pickup = packageData?.pickup_point;
    const drop = packageData?.drop_point;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "start", alignItems: "center", mb: 3 }}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIosNewIcon />}
                    onClick={() => navigate(-1)}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h5" fontWeight="bold">
                    Package Details
                </Typography>
            </Box>

            {/* Package Information */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Package Info
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary">Name</Typography>
                            <Typography>{packageData.package_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary">Reference ID</Typography>
                            <Typography>{packageData.reference_id}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary">Weight (kg)</Typography>
                            <Typography>{packageData.package_weight}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography color="text.secondary">Space (m³)</Typography>
                            <Typography>{packageData.package_space}</Typography>
                        </Grid>
                    </Box>
                </CardContent>
            </Card>

            {/* Pickup & Drop */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                        Route Info
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={3}>
                        {/* Pickup Point */}
                        {pickup && (
                            <Grid item xs={12} md={6}>
                                <Box display="flex" flexDirection="column" gap={1.2} p={2} sx={{ borderRadius: 2, bgcolor: "#fafafa" }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <RoomIcon color="primary" />
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Pickup Point
                                        </Typography>
                                    </Box>
                                    <Typography>{pickup.address}</Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Drop Point */}
                        <Grid item xs={12} md={6}>
                            <Box display="flex" flexDirection="column" gap={1.2} p={2} sx={{ borderRadius: 2, bgcolor: "#fafafa" }}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FlagIcon color="error" />
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Drop Point
                                    </Typography>
                                </Box>
                                {drop ? (
                                    <Typography>{drop.address}</Typography>
                                ) : (
                                    <Typography color="text.disabled">Not Assigned Yet</Typography>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Truck Info */}
            {truck && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                            <LocalShippingIcon sx={{ mr: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                                Truck Info
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Grid item xs={12} sm={4}>
                                <Typography color="text.secondary">Truck Name</Typography>
                                <Typography>{truck.truck_name}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography color="text.secondary">Truck No</Typography>
                                <Typography>{truck.truck_no}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Typography color="text.secondary">Capacity (kgs)</Typography>
                                <Typography>{truck.capacity_inkgs}</Typography>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Trip Info */}
            {trip && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                            <MapIcon sx={{ mr: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                                Trip Info
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography color="text.secondary">Start Date</Typography>
                                <Typography>{trip.start_date?.split("T")[0]}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography color="text.secondary">Distance (kms)</Typography>
                                <Typography>{trip.trip_in_kms}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography color="text.secondary">Duration (hours)</Typography>
                                <Typography>{trip.trip_duration_in_hours}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography color="text.secondary">Reach Date</Typography>
                                <Typography>
                                    {trip.reach_date ? trip.reach_date.split("T")[0] : "Pending"}
                                </Typography>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Payment Info */}
            {payment && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={1}>
                            <PaymentIcon sx={{ mr: 1 }} />
                            <Typography variant="h6" fontWeight="bold">
                                Payment Info
                            </Typography>
                        </Box>
                        <Divider sx={{ mb: 2 }} />
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                            <Grid item xs={12} sm={6}>
                                <Typography color="text.secondary">Order ID</Typography>
                                <Typography>{payment.order_id}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography color="text.secondary">Amount</Typography>
                                <Typography>₹{payment.amount}</Typography>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <Typography color="text.secondary">Status</Typography>
                                <Typography>{payment.status}</Typography>
                            </Grid>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
}

export default PackagesInfo;
